/**
 * O*NET attribution — single source of truth.
 *
 * USDOL/ETA's O*NET Web Services terms of service require the "O*NET in-it"
 * badge linking to services.onetcenter.org plus the trademark sentence on every
 * surface where O*NET-derived information is shown — including print output,
 * PDFs and exported data files.
 *
 * This module is plain TypeScript (no React) so exporters, PDF writers and CSV
 * writers can carry exactly the same wording as the on-screen component.
 *
 * Display and compliance only. Nothing here feeds scoring.
 */

export const ONET_SERVICES_URL = "https://services.onetcenter.org/";
export const ONET_BADGE_SRC = "https://www.onetcenter.org/image/link/onet-in-it.svg";
export const ONET_BADGE_TITLE =
  "This site incorporates information from O*NET Web Services. Click to learn more.";
export const ONET_BADGE_ALT = "O*NET in-it — information from O*NET Web Services";

/** The verbatim trademark sentence. Never reword this. */
export const ONET_ATTRIBUTION_SENTENCE =
  "This site incorporates information from O*NET Web Services by the U.S. Department of Labor, Employment and Training Administration (USDOL/ETA). O*NET® is a trademark of USDOL/ETA.";

/** Wording for documents and files, where "this site" would read oddly. */
export const ONET_ATTRIBUTION_DOCUMENT_SENTENCE =
  "This document incorporates information from O*NET Web Services by the U.S. Department of Labor, Employment and Training Administration (USDOL/ETA). O*NET® is a trademark of USDOL/ETA. " +
  ONET_SERVICES_URL;

/** Rows appended to any CSV export built from O*NET-derived content. */
export function onetCsvAttributionRows(): string[][] {
  return [
    [],
    ["O*NET attribution", ONET_ATTRIBUTION_DOCUMENT_SENTENCE],
    ["O*NET Web Services", ONET_SERVICES_URL],
  ];
}

/** Block added to any JSON export built from O*NET-derived content. */
export function onetJsonAttribution() {
  return {
    notice: ONET_ATTRIBUTION_DOCUMENT_SENTENCE,
    source: "O*NET Web Services",
    url: ONET_SERVICES_URL,
    trademark: "O*NET® is a trademark of USDOL/ETA.",
  };
}

/**
 * Every route that displays O*NET-derived content, with the file that must
 * render `<OnetAttribution />`. `src/test/onet-attribution.test.tsx` fails if a
 * listed surface drops the component, or if the route stops being registered.
 *
 * Add a row here the moment a new surface starts showing O*NET-derived data.
 */
export interface OnetSurface {
  /** Route pattern as registered in `src/App.tsx`. */
  route: string;
  /** File that must render the attribution component. */
  file: string;
  /** Why this surface carries O*NET-derived content. */
  reason: string;
}

export const ONET_CONTENT_SURFACES: OnetSurface[] = [
  {
    route: "/careers",
    file: "src/pages/CareersExplorer.tsx",
    reason: "Career listings, demand and skills descriptors",
  },
  {
    route: "/careers/:id",
    file: "src/pages/CareerDetail.tsx",
    reason: "Occupation detail, tasks, skills and outlook",
  },
  {
    route: "/skills",
    file: "src/pages/SkillsIntelligence.tsx",
    reason: "Skill mapping against O*NET / ESCO / ISCO",
  },
  {
    route: "/occupation-standards",
    file: "src/pages/OccupationStandards.tsx",
    reason: "Classification methodology page",
  },
  {
    route: "/assessment/v2/result",
    file: "src/pages/AssessmentV2Result.tsx",
    reason: "Career matches drawn from the classified corpus",
  },
  {
    route: "/card",
    file: "src/pages/CareerCardPage.tsx",
    reason: "Career Identity Card built from matched occupations",
  },
  {
    route: "/report",
    file: "src/components/report/LivingReport.tsx",
    reason: "Living Report career chapters",
  },
  {
    route: "/assessment/v2/dossier",
    file: "src/features/assessment-v2/components/DossierSections.tsx",
    reason: "Passport fitment sheets and provenance sheet (also /d/:token)",
  },
];
